<template>
    <div>
        <Card>
            <template #title>
                Backups
            </template>
            <template #content>
                <Toolbar class="mb-2 bg-gray-100">
                    <template #start>
                        <div class="flex align-items-center gap-2">
                            <Button icon="pi pi-heart" label="Monitorar" severity="info" size="small"
                                :loading="isMonitoring" @click="runMonitor" />
                            <i class="pi pi-question-circle text-gray-400"
                                v-tooltip.right="'Verifica idade do último backup e espaço livre no disco do servidor (alerta com 10% ou menos). Envia e-mail se houver irregularidade.'" />
                            <span
                                v-if="isBackupRunning"
                                class="flex align-items-center gap-1 text-sm text-orange-600 font-medium line-height-1"
                                v-tooltip.right="backupRunningTooltip"
                            >
                                <i class="pi pi-spin pi-spinner flex align-items-center flex-shrink-0 text-sm" aria-hidden="true" />
                                <span>{{ backupRunningLabel }}</span>
                            </span>
                        </div>
                    </template>
                    <template #end>
                        <div class="flex align-items-center gap-1">
                            <Button icon="pi pi-play" label="Fazer Backup" severity="success" size="small"
                                :loading="isRunning" :disabled="isBackupRunning" @click="openRunDialog" />
                            <Button icon="pi pi-refresh" label="Atualizar" severity="warn" size="small"
                                @click="loadData" />
                        </div>
                    </template>
                </Toolbar>

                <DataTable :value="lista" :loading="isLoading" size="small" stripedRows showGridlines scrollable
                    scrollHeight="calc(100vh - 300px)">

                    <Column style="width: 80px" field="id" header="#"></Column>

                    <Column field="type" header="Tipo">
                        <template #body="{ data }">
                            <Badge
                                :value="backupTypeLabel(data)"
                                :severity="backupTypeSeverity(data)"
                            />
                        </template>
                    </Column>

                    <Column field="generated_at" header="Data/Hora">
                        <template #body="{ data }">
                            {{ dateTimeFormat(data.generated_at) }}
                        </template>
                    </Column>

                    <Column field="file_size_formatted" header="Tamanho">
                        <template #body="{ data }">
                            {{ data.file_size_formatted ?? '—' }}
                        </template>
                    </Column>

                    <Column field="filename" header="Arquivo">
                        <template #body="{ data }">
                            <span class="text-sm font-mono text-gray-700">{{ data.filename ?? '—' }}</span>
                        </template>
                    </Column>

                    <Column field="status" header="Status">
                        <template #body="{ data }">
                            <template v-if="isStatusRunning(data)">
                                <span class="flex align-items-center gap-1">
                                    <Badge severity="warn" >
                                        <i class="pi pi-spin pi-spinner text-xs mr-1" aria-hidden="true" /> 
                                        Em andamento
                                    </Badge>
                                </span>
                            </template>
                            <template v-else>
                                <Badge
                                    :value="isBackupSuccess(data) ? 'Sucesso' : 'Falha'"
                                    :severity="isBackupSuccess(data) ? 'success' : 'danger'"
                                />
                            </template>
                        </template>
                    </Column>

                    <Column header="Ações" style="width: 200px" bodyClass="flex justify-content-end gap-1">
                        <template #body="{ data }">
                            <Button
                                v-if="isBackupSuccess(data)"
                                icon="pi pi-download"
                                severity="success"
                                size="small"
                                v-tooltip.top="hasBackupFile(data) ? 'Download' : 'Arquivo indisponível'"
                                :disabled="!hasBackupFile(data)"
                                :loading="downloadingId === data.id"
                                @click="downloadBackup(data)"
                            />
                            <Button
                                v-if="isBackupFailed(data)"
                                icon="pi pi-info-circle"
                                severity="danger"
                                size="small"
                                v-tooltip.top="'Ver erro'"
                                @click="showError(data)"
                            />
                            <Button
                                :disabled="isStatusRunning(data)"
                                icon="pi pi-trash"
                                severity="danger"
                                size="small"
                                v-tooltip.top="'Remover backup'"
                                :loading="removingId === data.id"
                                @click="confirmRemove(data)"
                            />
                        </template>
                    </Column>

                    <template #empty>
                        <div class="text-center text-gray-600">
                            Nenhum registro encontrado
                        </div>
                    </template>
                </DataTable>

                <div class="relative flex align-items-center justify-content-center mt-2 py-1">
                    <span class="absolute left-0 text-sm text-gray-600">{{ pagination.total }} registros</span>
                    <Paginator :first="pagination.first" :rows="pagination.perPage" :totalRecords="pagination.total"
                        @page="onPage" />
                </div>

            </template>
        </Card>
        <Dialog
            :visible="visibleRunDialog"
            @update:visible="visibleRunDialog = $event"
            header="Executar backup"
            modal
            class="w-11 lg:w-3"
        >
            <div v-if="isBackupRunning" class="flex align-items-center gap-2 p-3 border-round bg-orange-50 border-1 border-orange-200">
                <i class="pi pi-spin pi-spinner text-orange-600" />
                <span class="text-sm text-orange-700">
                    Já existe um backup em execução. Aguarde a conclusão antes de iniciar um novo.
                </span>
            </div>
            <div v-else class="flex flex-column gap-3">
                <div class="p-3 border-round bg-blue-50 border-1 border-blue-200">
                    <p class="m-0 text-sm text-blue-800 font-medium mb-1">
                        <i class="pi pi-database mr-1" /> Teste Rápido — Só Banco de Dados
                    </p>
                    <p class="m-0 text-sm text-blue-700">
                        Faz apenas o dump do PostgreSQL. Ideal para verificar se o backup está funcionando. Conclui em segundos.
                    </p>
                </div>
                <div class="p-3 border-round bg-gray-50 border-1 border-gray-200">
                    <p class="m-0 text-sm text-gray-800 font-medium mb-1">
                        <i class="pi pi-folder mr-1" /> Backup Completo — BD + Arquivos
                    </p>
                    <p class="m-0 text-sm text-gray-600">
                        Inclui banco de dados e todos os arquivos de contratos. Pode levar vários minutos (~126 mil arquivos).
                    </p>
                </div>
            </div>
            <template #footer>
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    severity="secondary"
                    @click="visibleRunDialog = false"
                />
                <Button
                    label="Teste Rápido (só BD)"
                    icon="pi pi-database"
                    severity="info"
                    :loading="isRunning"
                    :disabled="isBackupRunning"
                    @click="runBackup(true)"
                />
                <Button
                    label="Backup Completo"
                    icon="pi pi-play"
                    severity="success"
                    :loading="isRunning"
                    :disabled="isBackupRunning"
                    @click="runBackup(false)"
                />
            </template>
        </Dialog>
        <Dialog
            :visible="visibleRemoveDialog"
            @update:visible="onRemoveDialogVisibleChange"
            header="Remover backup"
            modal
            class="w-11 lg:w-3"
            :closable="!isRemoving"
        >
            <div class="flex flex-column gap-3">
                <p class="m-0 text-sm text-gray-600">
                    Esta ação não pode ser desfeita. Para confirmar, digite
                    <strong>remover</strong> no campo abaixo.
                    <span v-if="removeTarget && removeTarget.file_exists">
                        O arquivo também será excluído do servidor.
                    </span>
                </p>
                <InputText
                    v-model="removeConfirmText"
                    class="w-full md:w-15rem"
                    placeholder="Digite remover"
                    autocomplete="off"
                    :disabled="isRemoving"
                    @keyup.enter="submitRemove"
                />
            </div>
            <template #footer>
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    severity="secondary"
                    :disabled="isRemoving"
                    @click="closeRemoveDialog"
                />
                <Button
                    label="Remover"
                    icon="pi pi-trash"
                    severity="danger"
                    :loading="isRemoving"
                    :disabled="!canConfirmRemove"
                    @click="submitRemove"
                />
            </template>
        </Dialog>
        <Dialog
            :visible="visibleErrorDialog"
            @update:visible="visibleErrorDialog = $event"
            header="Detalhes da Falha"
            modal
            class="w-11 lg:w-5"
        >
            <div class="flex flex-column gap-3">
                <div v-if="selectedError">
                    <p class="text-sm text-gray-500 mb-1">Data/Hora do backup</p>
                    <p class="font-medium">{{ dateTimeFormat(selectedError.generated_at) }}</p>
                </div>
                <div>
                    <p class="text-sm text-gray-500 mb-1">Mensagem de erro</p>
                    <p class="font-medium text-red-600 whitespace-pre-wrap break-words">
                        {{ selectedError && selectedError.error_message ? selectedError.error_message : 'Sem detalhes disponíveis.' }}
                    </p>
                </div>
            </div>
            <template #footer>
                <Button label="Fechar" icon="pi pi-times" severity="secondary" @click="visibleErrorDialog = false" />
            </template>
        </Dialog>
    </div>
</template>
<script>
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import Button from 'primevue/button';
import Badge from 'primevue/badge';
import DataTable from 'primevue/datatable';
import Paginator from 'primevue/paginator';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';

import Backup from '@/service/backup.js';
import File from '@/utils/File.js';

import Moment from 'moment';

export default {
    components: {
        Card,
        Toolbar,
        Button,
        Badge,
        DataTable,
        Paginator,
        Column,
        Dialog,
        InputText,
    },
    data() {
        return {
            lista: [],
            isLoading: false,
            isRunning: false,
            isMonitoring: false,
            isBackupRunning: false,
            backupStartedAt: null,
            statusInterval: null,
            downloadingId: null,
            removingId: null,
            visibleRunDialog: false,
            visibleRemoveDialog: false,
            removeTarget: null,
            removeConfirmText: '',
            isRemoving: false,
            visibleErrorDialog: false,
            selectedError: null,
            pagination: {
                page: 0,
                perPage: 0,
                total: 0,
                first: 0,
            },
        };
    },
    computed: {
        backupRunningLabel() {
            if (!this.backupStartedAt) {
                return 'Backup em execução...';
            }

            const inicio = Moment.utc(this.backupStartedAt).local().format('DD/MM/YYYY HH:mm');
            return `Backup em execução desde ${inicio}`;
        },
        backupRunningTooltip() {
            return [
                'O sistema consulta o status a cada 5 segundos.',
                'O aviso some quando o backup termina (sucesso ou falha) e a lista é atualizada.',
                '',
                'Para confirmar no servidor:',
                '• worker gtsystem-backup-worker na fila "backup" (Supervisor)',
                '• log: storage/logs/backup.log',
                '• comando: php artisan backup:list',
                '',
                'Se ficar preso por horas sem novo registro na lista, verifique o worker da fila ou limpe o cache: php artisan cache:forget backup_running',
            ].join('\n');
        },
        canConfirmRemove() {
            return this.removeConfirmText.trim().toLowerCase() === 'remover';
        },
    },
    beforeMount() {
        this.pagination.page = parseInt(this.$route.query.page || 0);
    },
    mounted() {
        this.loadData();
        this.checkStatus();
    },
    beforeUnmount() {
        clearInterval(this.statusInterval);
    },
    methods: {
        applyBackupStatus(payload) {
            const running = !!payload?.running;
            this.isBackupRunning = running;
            this.backupStartedAt = running ? (payload?.started_at ?? null) : null;
        },
        checkStatus() {
            Backup.status()
                .then(response => {
                    this.applyBackupStatus(response.data);

                    if (this.isBackupRunning) {
                        if (!this.statusInterval) {
                            this.statusInterval = setInterval(() => {
                                Backup.status()
                                    .then(r => {
                                        this.applyBackupStatus(r.data);
                                        if (!this.isBackupRunning) {
                                            clearInterval(this.statusInterval);
                                            this.statusInterval = null;
                                            this.loadData();
                                            this.$toast.add({
                                                severity: 'success',
                                                summary: 'Backup concluído',
                                                detail: 'A lista foi atualizada.',
                                                life: 4000,
                                            });
                                        }
                                    })
                                    .catch(() => {});
                            }, 5000);
                        }
                    } else {
                        if (this.statusInterval) {
                            clearInterval(this.statusInterval);
                            this.statusInterval = null;
                        }
                    }
                })
                .catch(() => {});
        },

        runMonitor() {
            this.isMonitoring = true;
            Backup.monitor()
                .then(response => {
                    const status = response.data.status;
                    const message = response.data.message || response.data.msg;
                    if (status === 'healthy') {
                        this.$toast.add({ severity: 'success', summary: 'Monitoramento', detail: message, life: 5000 });
                    } else {
                        this.$toast.add({ severity: 'warn', summary: 'Atenção', detail: message, life: 5000 });
                    }
                })
                .catch(error => {
                    if (error.response && error.response.data.msg) {
                        this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                        return;
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isMonitoring = false;
                });
        },

        openRunDialog() {
            Backup.status()
                .then(response => {
                    const running = !!response.data?.running;
                    this.isBackupRunning = running;
                    this.backupStartedAt = running ? (response.data?.started_at ?? null) : null;
                })
                .finally(() => {
                    this.visibleRunDialog = true;
                });
        },

        runBackup(onlyDb = false) {
            this.isRunning = true;
            Backup.run(onlyDb)
                .then(() => {
                    this.visibleRunDialog = false;
                    this.$toast.add({ severity: 'success', summary: 'Backup enfileirado!', detail: 'O backup será executado em background pelo worker.', life: 6000 });
                    this.checkStatus();
                })
                .catch(error => {
                    if (error.response && error.response.data.msg) {
                        this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                        return;
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isRunning = false;
                });
        },

        loadDataParams() {
            const params = [];
            params.push(`page=${1 + this.pagination.page}`);
            return params;
        },

        loadData() {
            const params = this.loadDataParams();

            this.isLoading = true;
            Backup.lista(params.join('&'))
                .then(response => {
                    this.lista = response.data.data.map(item => ({
                        ...item,
                        file_exists: item.file_exists === true
                            || item.file_exists === 1
                            || item.file_exists === '1',
                    }));
                    this.pagination.perPage = response.data.per_page;
                    this.pagination.total = response.data.total;
                    this.pagination.first = response.data.per_page * this.pagination.page;
                })
                .catch(error => {
                    if (error.response && error.response.data.msg) {
                        this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                        return;
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        downloadBackup(data) {
            this.downloadingId = data.id;
            Backup.downloadToken(data.id)
                .then(response => {
                    window.location.href = response.data.url;
                })
                .catch(error => {
                    if (error.response && error.response.data.msg) {
                        this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                        return;
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.downloadingId = null;
                });
        },

        onPage(event) {
            const page = event ? event.page : 0;
            this.pagination.page = page;
            this.$router.replace({ name: this.$route.name, query: { page: page } });
            this.loadData();
        },

        dateTimeFormat(value) {
            if (!value) return '—';
            return Moment.utc(value).local().format('DD/MM/YYYY HH:mm');
        },

        backupStatus(data) {
            return String(data?.status ?? '').toLowerCase();
        },

        backupType(data) {
            return String(data?.type ?? '').toLowerCase();
        },

        backupTypeLabel(data) {
            const type = this.backupType(data);
            const fileCount = Number(data?.application_files_count ?? 0);

            if (type === 'full' || (type === 'sql' && fileCount > 0)) {
                return fileCount > 0
                    ? `SQL + Arquivos (${fileCount})`
                    : 'SQL + Arquivos';
            }

            const labels = {
                sql: 'SQL',
                files: 'Arquivos',
                full: 'SQL + Arquivos',
            };

            return labels[type] ?? 'Arquivos';
        },

        backupTypeSeverity(data) {
            const type = this.backupType(data);
            const fileCount = Number(data?.application_files_count ?? 0);

            if (type === 'full' || (type === 'sql' && fileCount > 0)) {
                return 'success';
            }

            if (type === 'sql') {
                return 'info';
            }

            return 'warn';
        },

        isBackupFailed(data) {
            return this.backupStatus(data) === 'failed';
        },

        isBackupSuccess(data) {
            return this.backupStatus(data) === 'success';
        },

        isStatusRunning(data) {
            return this.backupStatus(data) === 'running';
        },

        hasBackupFile(data) {
            return data?.file_exists === true || data?.file_exists === 1 || data?.file_exists === '1';
        },

        showError(data) {
            this.selectedError = data;
            this.visibleErrorDialog = true;
        },

        confirmRemove(data) {
            this.removeTarget = data;
            this.removeConfirmText = '';
            this.visibleRemoveDialog = true;
        },

        closeRemoveDialog() {
            this.visibleRemoveDialog = false;
            this.removeTarget = null;
            this.removeConfirmText = '';
        },

        onRemoveDialogVisibleChange(visible) {
            if (!visible) {
                this.closeRemoveDialog();
                return;
            }
            this.visibleRemoveDialog = visible;
        },

        submitRemove() {
            if (!this.removeTarget || !this.canConfirmRemove || this.isRemoving) {
                return;
            }

            this.isRemoving = true;
            this.removingId = this.removeTarget.id;

            Backup.remove(this.removeTarget.id, this.removeConfirmText.trim().toLowerCase())
                .then(() => {
                    this.closeRemoveDialog();
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Removido',
                        detail: 'Backup removido do banco e do disco.',
                        life: 4000,
                    });
                    this.loadData();
                })
                .catch(error => {
                    if (error.response?.data?.msg) {
                        this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.response.data.msg, life: 5000 });
                        return;
                    }
                    if (error.response?.data?.errors?.confirmacao?.[0]) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Ops!',
                            detail: error.response.data.errors.confirmacao[0],
                            life: 5000,
                        });
                        return;
                    }
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: error.message, life: 5000 });
                })
                .finally(() => {
                    this.isRemoving = false;
                    this.removingId = null;
                });
        },
    },
};
</script>
